import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getTextField } from '@lender/utils';
import { Box, Typography, useTheme } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import ModalFormWrapper from '@sharedCR/coreDesignSystem/ModalFormWrapper';
import { FormBuilderSkeleton } from 'app/pages/system/forms';
import { formApi, formsApi } from 'app/services/api';
import DocumentView from 'app/shared/components/document/DocumentView';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { object } from 'yup';

import { FeatureFlag } from 'constants/featureFlags';
import { FieldType } from 'constants/formGenerator';
import { KEY_CODES } from 'constants/keyboard';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';
import { getServerErrorMessage } from 'utils/error';
import { getPressedKey } from 'utils/keyboard';

import { getFormsAction } from 'store/forms/actions';
import { selectForms } from 'store/forms/selectors';
import { selectFeatureFlagByKey } from 'store/global/selectors';

import { getStyles } from './FormPreviewModal.styles';

const preventSubmitHandler = e => {
  if (getPressedKey(e) === KEY_CODES.ENTER) {
    e.preventDefault();
  }
};

const FormPreviewModal = ({
  setIsLoading,
  isLoading,
  previewForms,
  formRef,
  applicationId,
  onProceed,
}) => {
  const dispatch = useDispatch();

  const forms = useSelector(selectForms);
  const isFormsConsolidationEnabled = useSelector(
    selectFeatureFlagByKey(FeatureFlag.FormsConsolidationEnabled),
  );

  const previewApi = isFormsConsolidationEnabled ? formsApi : formApi;

  const [isInitialized, setIsInitialized] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const theme = useTheme();

  const classes = getStyles(theme);

  const { previewFormsOptions, initialFormValues } = useMemo(
    () =>
      previewForms.reduce(
        (acc, { formId }) => {
          const formRecord = forms[formId];

          if (!formRecord) {
            return acc;
          }

          const formOption = { value: formRecord.formId, name: formRecord.name };

          acc.previewFormsOptions.push(formOption);

          if (formId === previewForms[0]?.formId) {
            acc.initialFormValues = { selectedForm: formOption };
          }

          return acc;
        },
        { previewFormsOptions: [], initialFormValues: { selectedForm: null } },
      ),
    [previewForms, forms],
  );

  const {
    form,
    setForm,
    setInitialFormValues,
    setValidationErrors,
    setSchema,
    setInForm,
    validateField,
    validationErrors,
    handleChange,
  } = useForm(initialFormValues);

  const onPreviewFormInputChange = useCallback(
    (e, option) => {
      setIsError(false);

      onAutocompleteChangeInForm({ validateField, setInForm })(e, option);
    },
    [setInForm, validateField, setIsError],
  );

  const previewConfig = useMemo(
    () => ({
      groups: [
        {
          type: FieldType.GROUP,
          key: 'preview-select',
          fields: [
            {
              type: FieldType.SELECT,
              nameInModel: 'selectedForm',
              inputProps: {
                label: 'Select Form to Preview',
                required: true,
                disabled: isLoading,
                options: previewFormsOptions,
                variant: 'outlined',
                widthInColumns: 1,
                disablePortal: false,
                customOnChange: onPreviewFormInputChange,
              },
            },
          ],
        },
      ],
    }),
    [isLoading, previewFormsOptions, onPreviewFormInputChange],
  );

  const selectedFormId = form.selectedForm?.value;

  const getDocumentCall = useCallback(() => {
    if (!selectedFormId) {
      return Promise.reject();
    }

    setIsError(false);
    setErrorMessage(null);

    return previewApi
      .getGeneratedFormByApplicationId({
        applicationId,
        formId: selectedFormId,
      })
      .then(({ data }) => ({ data }))
      .catch(async err => {
        setErrorMessage(await getServerErrorMessage(err));
        setIsError(true);

        return { data: null };
      });
  }, [previewApi, applicationId, selectedFormId]);

  const onSubmitForm = useCallback(
    async e => {
      e.preventDefault();

      setIsLoading(true);
      setIsError(false);

      onProceed(form).finally(() => setIsLoading(false));
    },
    [form, onProceed, setIsLoading],
  );

  useEffect(() => {
    setSchema(
      object().shape({
        selectedForm: getTextField({
          isRequired: true,
          errorText: 'Form can not be empty',
        }).nullable(),
      }),
    );
  }, [setSchema]);

  useEffect(() => {
    setForm(initialFormValues);
    setInitialFormValues(initialFormValues);
    setValidationErrors({});
  }, [initialFormValues, setInitialFormValues, setForm, setValidationErrors]);

  useEffectSafeStrictMode(() => {
    setIsLoading(true);

    dispatch(getFormsAction({ isDeleted: false })).finally(() => {
      setIsLoading(false);

      setIsInitialized(true);
    });
  }, [dispatch, setIsLoading]);

  if (isLoading || !isInitialized) {
    return <FormBuilderSkeleton />;
  }

  return (
    <ModalFormWrapper
      isFormChanged
      formRef={formRef}
      formTestId="generate-forms-preview"
      isSubmitting={isLoading}
      submitButtonName="Proceed"
      onKeyDown={preventSubmitHandler}
      onSubmit={onSubmitForm}
    >
      {previewForms.length > 1 && (
        <FormGenerator
          isNestedForm
          config={previewConfig}
          form={form}
          handleChange={handleChange}
          numberOfColumns={1}
          setInForm={setInForm}
          validateField={validateField}
          validationErrors={validationErrors}
        />
      )}
      {!isError && (
        <Box sx={classes.root}>
          <DocumentView
            isView
            supressErrorMessage
            docId="form-preview"
            getDocumentCall={getDocumentCall}
            isControlVisible={false}
            isDownloadable={false}
            isPrintable={false}
          />
        </Box>
      )}
      {isError && (
        <Typography color="error" variant="medium12">
          {errorMessage || 'Failed to generate form preview.'}
        </Typography>
      )}
    </ModalFormWrapper>
  );
};

FormPreviewModal.propTypes = {
  ...WithLoadingPropTypes,
  previewForms: PropTypes.arrayOf(
    PropTypes.shape({
      formId: PropTypes.string.isRequired,
    }),
  ).isRequired,
  applicationId: PropTypes.string.isRequired,
  onProceed: PropTypes.func.isRequired,
};

export default compose(withLoading, memo)(FormPreviewModal);
